import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Text,
  VStack,
  Spinner,
  InputGroup,
  Grid,
  GridItem,
} from '@chakra-ui/react';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { vehicleService, Transaction, Segurado, Emission } from '../services/vehicleService';
import { toaster } from '../components/toaster';
import { addressService, City } from '../services/addressService';
import { Header } from '../components/Header';

// Definindo componentes alternativos já que o FormControl não está disponível
const FormControl = ({ children, isInvalid, isRequired }: { children: React.ReactNode, isInvalid?: boolean, isRequired?: boolean }) => (
  <Box>
    {children}
  </Box>
);

const FormLabel = ({ children, fontSize, color, mb, isRequired = true, fontWeight, textTransform, letterSpacing }: { 
  children: React.ReactNode, 
  fontSize?: string, 
  color?: string, 
  mb?: number,
  isRequired?: boolean,
  fontWeight?: string,
  textTransform?: string,
  letterSpacing?: string
}) => (
  <Text fontSize={fontSize} color={color} mb={mb} fontWeight={fontWeight} textTransform={textTransform as any} letterSpacing={letterSpacing}>
    {children}
    {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
  </Text>
);

const FormErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <Text color="red.500" fontSize="xs" mt={1}>{children}</Text>
);

// Função para validar CPF
const isValidCPF = (cpf: string): boolean => {
  try {
    if (!cpf || typeof cpf !== 'string') return false;
    
    // Remove caracteres especiais
    const cleanCpf = cpf.replace(/[^\d]+/g, '');
    
    // Verifica se tem 11 dígitos
    if (cleanCpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleanCpf)) return false;
    
    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      const digit = parseInt(cleanCpf.charAt(i));
      if (isNaN(digit)) return false;
      sum += digit * (10 - i);
    }
    let mod = sum % 11;
    let digit = mod < 2 ? 0 : 11 - mod;
    if (parseInt(cleanCpf.charAt(9)) !== digit) return false;
    
    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      const digit = parseInt(cleanCpf.charAt(i));
      if (isNaN(digit)) return false;
      sum += digit * (11 - i);
    }
    mod = sum % 11;
    digit = mod < 2 ? 0 : 11 - mod;
    if (parseInt(cleanCpf.charAt(10)) !== digit) return false;
    
    return true;
  } catch (error) {
    console.error('Erro na validação de CPF:', error);
    return false;
  }
};

// Função para validar CNPJ
const isValidCNPJ = (cnpj: string): boolean => {
  try {
    if (!cnpj || typeof cnpj !== 'string') return false;
    
    // Remove caracteres especiais
    const cleanCnpj = cnpj.replace(/[^\d]+/g, '');
    
    // Verifica se tem 14 dígitos
    if (cleanCnpj.length !== 14) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleanCnpj)) return false;
    
    // Validação do primeiro dígito verificador
    let size = cleanCnpj.length - 2;
    let numbers = cleanCnpj.substring(0, size);
    const digits = cleanCnpj.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      const digit = parseInt(numbers.charAt(size - i));
      if (isNaN(digit)) return false;
      sum += digit * pos--;
      if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    const firstDigit = parseInt(digits.charAt(0));
    if (isNaN(firstDigit) || result !== firstDigit) return false;
    
    // Validação do segundo dígito verificador
    size = size + 1;
    numbers = cleanCnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      const digit = parseInt(numbers.charAt(size - i));
      if (isNaN(digit)) return false;
      sum += digit * pos--;
      if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    const secondDigit = parseInt(digits.charAt(1));
    if (isNaN(secondDigit) || result !== secondDigit) return false;
    
    return true;
  } catch (error) {
    console.error('Erro na validação de CNPJ:', error);
    return false;
  }
};

// Função para validar CPF ou CNPJ
const isValidDocument = (document: string, type: string): boolean => {
  try {
    // Se não houver documento, não validar
    if (!document || typeof document !== 'string') return false;
    
    // Remove caracteres especiais
    const cleanDoc = document.replace(/[^\d]+/g, '');
    
    // Detecta automaticamente o tipo de documento pelo tamanho
    if (cleanDoc.length === 11) {
      // É um CPF
      console.log('Validando como CPF:', cleanDoc);
      const result = isValidCPF(cleanDoc);
      return typeof result === 'boolean' ? result : false;
    } else if (cleanDoc.length === 14) {
      // É um CNPJ
      console.log('Validando como CNPJ:', cleanDoc);
      const result = isValidCNPJ(cleanDoc);
      return typeof result === 'boolean' ? result : false;
    } else if (cleanDoc.length < 11) {
      // Documento incompleto, não validar ainda
      console.log('Documento incompleto, não validando ainda');
      return true;
    }
    
    console.log('Documento com tamanho inválido:', cleanDoc.length);
    return false;
  } catch (error) {
    console.error('Erro na validação de documento:', error);
    return false;
  }
};

// Formata CPF (11 dígitos) ou CNPJ (14 dígitos) para exibição
const formatCpfCnpj = (value: string | undefined): string => {
  if (!value) return '';
  const digits = value.replace(/[^\d]+/g, '');
  if (digits.length <= 11) {
    // Máscara CPF: 000.000.000-00
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  if (digits.length >= 14) {
    // Máscara CNPJ: 00.000.000/0000-00
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
  }
  return value;
};

// Schema de validação Yup
const validationSchema = Yup.object({
  CustomerType: Yup.string().required('Tipo de Cliente é obrigatório'),
  CustomerName: Yup.string().required('Nome é obrigatório'),
  DateOfBirth: Yup.string()
    .required('Data de nascimento é obrigatória')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato inválido: use AAAA-MM-DD')
    .test('valid-date', 'Data inválida', (value) => {
      if (!value) return false;
      const date = new Date(value);
      return !isNaN(date.getTime()) && date < new Date();
    }),
  IdNo: Yup.string()
    .required('Documento é obrigatório')
    .test('valid-document', 'Documento inválido', function(value) {
      // Se não houver valor ou for muito curto, não validar ainda
      if (!value || value.replace(/[^\d]+/g, '').length < 11) {
        console.log('Documento muito curto ou vazio, não validando ainda');
        return true;
      }
      
      // Valida o documento automaticamente detectando o tipo pelo tamanho
      const isValid = isValidDocument(value, '');
      console.log('Resultado da validação:', isValid);
      return isValid;
    }),
    NumeroDocumento: Yup.string(), // Campo não obrigatório
    OrgaoExpedidor: Yup.string(), // Campo não obrigatório
    Mobile: Yup.string()
    .required('Telefone é obrigatório')
    .matches(/^\d{10,11}$/, 'Telefone deve conter entre 10 e 11 dígitos'),
  Email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório'),
  PostCode: Yup.string()
    .required('CEP é obrigatório')
    .matches(/^\d{8}$/, 'CEP deve conter 8 dígitos'),
  Address: Yup.string().required('Endereço é obrigatório'),
  AddressNumber: Yup.string().required('Número é obrigatório'),
  Suburb: Yup.string().required('Bairro é obrigatório'),
  State: Yup.string().required('Estado é obrigatório'),
  City: Yup.string().required('Cidade é obrigatória'),
  Gender: Yup.string().required('Gênero é obrigatório'),
  Nationality: Yup.string().required('Nacionalidade é obrigatória'),
  EstadoCivil: Yup.string().required('Estado Civil é obrigatório'),
  IsPolicyHolder: Yup.string().required('Campo obrigatório'),
  IsInsured: Yup.string().required('Campo obrigatório'),
  IsCoInsured: Yup.string().required('Campo obrigatório'),
  IsBeneficiary: Yup.string().required('Campo obrigatório'),
  IsPep: Yup.string().required('Campo obrigatório'),
  ConsentData: Yup.string().required('Campo obrigatório'),
  ConsentEmail: Yup.string().required('Campo obrigatório')
});

// Interface para os dados do comprador
interface CustomerData {
  CustomerType: string;
  CustomerName: string;
  DateOfBirth: string;
  IdNo: string;
  NumeroDocumento: string;
  OrgaoExpedidor: string;
  IsPolicyHolder: string;
  IsInsured: string;
  IsCoInsured: string;
  IsBeneficiary: string;
  IsPep: string;
  ConsentData: string;
  ConsentEmail: string;
  Mobile: string;
  Email: string;
  PostCode: string;
  Address: string;
  AddressNumber: string;
  AddressComplement: string;
  Suburb: string;
  State: string;
  City: string;
  Gender: string;
  Nationality: string;
  EstadoCivil: string;
}

export const HomePage: React.FC = () => {
  // Desabilitar temporariamente a etapa de Planos/Upgrade.
  // Para reativar no futuro, basta mudar para `true`.
  const UPGRADE_ENABLED = false;

  const [searchValue, setSearchValue] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingEmissionId, setDownloadingEmissionId] = useState<number | string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados para upgrade de plano
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<string>('');
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState<{type: 'success' | 'info', message: string} | null>(null);
  const lastUpgradeRef = useRef<{
    applied: boolean;
    contractId?: number | string;
    fromPlanId?: string;
    fromPlanCode?: string;
    toPlanId?: string;
  }>({ applied: false });
  
  // Estados para endereço
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  
  // Estado para erros de validação com validação defensiva
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Estado para controlar o wizard/steps
  const [currentStep, setCurrentStep] = useState<'search' | 'details' | 'upgrade' | 'customer' | 'effectivation' | 'success'>('search');
  
  // Estado para armazenar informações de sucesso
  const [successInfo, setSuccessInfo] = useState<{
    transactionId: number | string;
    policyNo: string;
    customerName?: string;
    idNo?: string;
    contractNo?: string;
    emissions?: Emission[];
  } | null>(null);
  
  // Função segura para definir erros
  const setFieldErrorsSafe = (errors: Record<string, string>) => {
    const safeErrors: Record<string, string> = {};
    Object.keys(errors).forEach(key => {
      const error = errors[key];
      safeErrors[key] = typeof error === 'string' ? error : 'Informação inválida. Verifique os dados informados.';
    });
    setFieldErrors(safeErrors);
  };

  // Estado para os dados do comprador com Formik
  const formik = useFormik({
    initialValues: {
      CustomerType: '',
      CustomerName: '',
      DateOfBirth: '',
      IdNo: '',
      NumeroDocumento: '',
      OrgaoExpedidor: '',
      IsPolicyHolder: 'N',
      IsInsured: 'Y',
      IsCoInsured: 'N',
      IsBeneficiary: 'Y',
      IsPep: 'N',
      ConsentData: 'Y',
      ConsentEmail: 'Y',
      Mobile: '',
      Email: '',
      PostCode: '',
      Address: '',
      AddressNumber: '',
      AddressComplement: '',
      Suburb: '',
      State: '',
      City: '',
      Gender: '',
      Nationality: '',
      EstadoCivil: ''
    } as CustomerData,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: values => {
      // Não vamos fazer submit direto, apenas usamos para validação
    }
  });

  // Para manter compatibilidade com o código existente
  const customerData = React.useMemo(() => {
    const values = formik.values;
    // Garantir que todos os valores sejam strings válidas
    const safeValues: Record<string, string> = {};
    Object.keys(values).forEach(key => {
      const value = values[key as keyof typeof values];
      safeValues[key] = String(value || '');
    });
    return safeValues;
  }, [formik.values]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (file && file.type !== 'application/pdf') {
      toaster.create({ type: 'error', title: 'Apenas arquivos PDF são aceitos' });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setSelectedFile(file);
    if (file) {
      toaster.create({ type: 'success', title: `Arquivo "${file.name}" selecionado` });
    }
  };
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEffectiveDate(e.target.value);
  };
  
  // Função para buscar cidades por UF
  const fetchCities = async (uf: string) => {
    try {
      setLoadingCities(true);
      const citiesList = await addressService.getCitiesByUF(uf);
      setCities(citiesList);
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      toaster.create({ type: 'error', title: 'Não foi possível carregar as cidades para o estado selecionado' });
    } finally {
      setLoadingCities(false);
    }
  };
  
  // Função para atualizar os dados do comprador
  const handleCustomerDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Atualizar apenas o valor do campo sem validação imediata
    formik.setFieldValue(name, value);
    
    // Se o campo for Estado, buscar cidades
    if (name === 'State' && value) {
      fetchCities(value);
      
      // Limpar o valor da cidade se o estado mudar
      formik.setFieldValue('City', '');
    }
    
    // Marcar como tocado, mas não validar imediatamente
    formik.setFieldTouched(name, true, false);
  };
  
  // Função para baixar apólice(s)
  const handleDownloadPolicy = async (transactionId: number | string, emissionId?: number | string) => {
    try {
      // Se foi fornecido um emissionId específico, baixar apenas essa emissão
      if (emissionId !== undefined) {
        setDownloadingEmissionId(emissionId);
        console.log(`Baixando apólice da emissão - ID: ${emissionId}`);
        await vehicleService.downloadPolicy(transactionId, String(emissionId));
        
        toaster.create({ type: 'success', title: 'Apólice baixada com sucesso!' });
      } else {
        setIsLoading(true);
        // Buscar emissions do successInfo ou da transação atual
        let emissions = successInfo?.emissions;
        
        // Se não tiver no successInfo, buscar da transação (caso da tela de detalhes)
        if (!emissions) {
          const transaction = transactions.find(t => t.id === transactionId);
          emissions = transaction?.emissions;
        }
        
        // Verificar se há múltiplas emissões (caso de upgrade)
        if (emissions && emissions.length > 0) {
          console.log(`Baixando ${emissions.length} apólice(s) das emissões:`, emissions);
          
          // Baixar todas as apólices das emissões
          for (const emission of emissions) {
            console.log(`Baixando apólice da emissão - ID: ${emission.id}, Plano: ${emission.planCode}, Apólice: ${emission.policyNo}`);
            await vehicleService.downloadPolicy(transactionId, emission.id);
            // Pequeno delay entre downloads para evitar problemas
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          toaster.create({ type: 'success', title: `${emissions.length} apólice(s) baixada(s) com sucesso!` });
        } else {
          // Caso padrão: baixar apenas uma apólice
          console.log(`Baixando apólice única para transação ID: ${transactionId}`);
          await vehicleService.downloadPolicy(transactionId);
          
          toaster.create({ type: 'success', title: 'Apólice baixada com sucesso!' });
        }
      }
    } catch (error) {
      console.error('Erro ao baixar apólice:', error);
      toaster.create({ type: 'error', title: 'Não foi possível baixar a apólice. Por favor, tente novamente.' });
    } finally {
      setIsLoading(false);
      setDownloadingEmissionId(null);
    }
  };
  
  // Função para validar um campo específico
  const validateField = (name: string, value: string | number | boolean) => {
    // Limpar erro existente
    const newErrors = { ...fieldErrors };
    delete newErrors[name];
    
    // Validar campo específico
    try {
      // Somente validar se for um campo conhecido no schema
      if (Object.keys(validationSchema.fields).includes(name)) {
        // Converter valor para string se necessário
        const stringValue = String(value || '');
        
        // Validar o valor contra o schema completo, mas capturar o erro apenas para o campo específico
        validationSchema.validateSyncAt(name, { [name]: stringValue });
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        // Garantir que a mensagem de erro seja uma string
        const errorMessage = typeof error.message === 'string' ? error.message : 'Informação inválida. Verifique os dados informados.';
        newErrors[name] = errorMessage;
      } else {
        // Para outros tipos de erro, usar uma mensagem padrão
        newErrors[name] = 'Informação inválida. Verifique os dados informados.';
      }
    }
    
    setFieldErrorsSafe(newErrors);
  };
  
  // Função para mapear Gender (M, F, O para 1, 2, 3)
  const mapGender = (gender: string): string => {
    switch(gender) {
      case 'M':
        return '1';
      case 'F':
        return '2';
      case 'O':
        return '3';
      default:
        return gender;
    }
  };

  // Função para obter informações amigáveis sobre o plano de upgrade
  const getUpgradePlanInfo = (planCode: string) => {
    if (!planCode) {
      return {
        title: 'Upgrade de Plano',
        duration: 'Cobertura estendida',
        description: 'Aumente seu período de proteção',
        benefits: ['Mais dias de cobertura'],
        badge: 'Upgrade',
        color: '#3b82f6'
      };
    }
    
    const upperCode = planCode.toUpperCase();
    
    // Verificar por 360 dias (1 ano)
    if (upperCode.includes('U360') || upperCode.includes('360')) {
      return {
        title: 'Proteção Estendida - 360 Dias',
        duration: '1 ano completo',
        description: 'Amplie sua cobertura para 360 dias de proteção total',
        benefits: [
          'Cobertura por 1 ano completo',
          'Mais tranquilidade e segurança',
          'Melhor custo-benefício'
        ],
        badge: '12 meses',
        color: '#10b981'
      };
    } 
    
    // Verificar por 720 dias (2 anos)
    if (upperCode.includes('U720') || upperCode.includes('720')) {
      return {
        title: 'Proteção Premium - 720 Dias',
        duration: '2 anos completos',
        description: 'Máxima proteção com 720 dias de cobertura ininterrupta',
        benefits: [
          'Cobertura por 2 anos completos',
          'Economia a longo prazo',
          'Proteção máxima garantida',
          'Plano mais vantajoso'
        ],
        badge: '24 meses',
        color: '#8b5cf6'
      };
    }
    
    // Fallback para outros planos - tentar extrair dias do código
    const daysMatch = upperCode.match(/(\d+)/);
    const days = daysMatch ? parseInt(daysMatch[1]) : 0;
    
    return {
      title: 'Upgrade de Plano',
      duration: days > 0 ? `${days} dias de cobertura` : 'Cobertura estendida',
      description: 'Aumente seu período de proteção',
      benefits: [
        days > 0 ? `Cobertura por ${days} dias` : 'Mais dias de cobertura',
        'Maior proteção para seu veículo'
      ],
      badge: days > 0 ? `${days} dias` : 'Upgrade',
      color: '#3b82f6'
    };
  };

  // Função para verificar se todos os dados do comprador estão preenchidos e validados
  const isCustomerDataComplete = () => {
    // Se for DEALER, não precisa validar dados do cliente
    const transaction = transactions?.[0];
    if (transaction?.planInsuranceHolder === "DEALER") {
      return true;
    }
    
    // Campos obrigatórios (apenas para CUSTOMER)
    const requiredFields: (keyof CustomerData)[] = [
      'CustomerType',
      'CustomerName',
      'DateOfBirth',
      'IdNo',
      'Mobile',
      'Email',
      'PostCode',
      'Address',
      'AddressNumber',
      'Suburb',
      'State',
      'City',
      'Gender',
      'Nationality',
      'EstadoCivil'
    ];
    
    // Primeiro verifica se todos os campos obrigatórios estão preenchidos
    const allFieldsFilled = requiredFields.every(field => {
      const value = customerData?.[field as keyof typeof customerData];
      return value !== '' && value !== null && value !== undefined;
    });
    
    if (!allFieldsFilled) return false;
    
    // Depois executa a validação completa de todos os campos
    try {
      // Validar dados do formulário usando o schema Yup
      validationSchema.validateSync(formik.values, { abortEarly: false });
      return true;
    } catch (error) {
      // Não chamar setFieldErrors aqui pois esta função é chamada durante o render
      // (via props disabled/opacity), causando o React error #301.
      // Os erros de validação são exibidos via onBlur de cada campo.
      return false;
    }
  };
  
  const handleSubmitDocuments = async () => {
    if (!selectedFile) {
      toaster.create({ type: 'warning', title: 'Por favor, selecione um arquivo PDF da nota fiscal' });
      return;
    }

    if (!effectiveDate) {
      toaster.create({ type: 'warning', title: 'Por favor, informe a data de efetivação' });
      return;
    }

    // Verificar se há uma transação selecionada
    if (!transactions || transactions.length === 0) {
      toaster.create({ type: 'error', title: 'Nenhuma transação encontrada para envio de documentos' });
      return;
    }

    // Usar a primeira transação encontrada
    const transaction = transactions?.[0];

    // Para CUSTOMER, validar dados do comprador; para DEALER, não é necessário
    if (transaction?.planInsuranceHolder === "CUSTOMER" && !isCustomerDataComplete()) {
      toaster.create({ type: 'warning', title: 'Por favor, preencha todos os dados do comprador' });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Chamar o serviço de upload do arquivo PDF
      if (!transaction) {
        toaster.create({ type: 'error', title: 'Transação não encontrada' });
        return;
      }
      
      await vehicleService.uploadInvoicePDF(
        transaction.id,
        selectedFile,
        effectiveDate
      );
      
      // Preparar os dados do segurado apenas se for CUSTOMER
      let seguradoData: Segurado | undefined = undefined;
      
      if (transaction.planInsuranceHolder === "CUSTOMER") {
        seguradoData = {
          CpfCnpj: customerData.IdNo.replace(/[^\d]+/g, ''), // Remove caracteres não numéricos
          NumeroDocumento: customerData.NumeroDocumento || '', // Usar o valor inserido pelo usuário
          OrgaoEmissorDocumento: customerData.OrgaoExpedidor, // Usar o órgão expedidor inserido pelo usuário
          Nome: customerData.CustomerName,
          DataNascimento: customerData.DateOfBirth,
          Genero: parseInt(mapGender(customerData.Gender)), // Converter para número
          EstadoCivil: parseInt(customerData.EstadoCivil), // Usar o estado civil selecionado pelo usuário
          Endereco: {
            Endereco: customerData.Address,
            Numero: customerData.AddressNumber,
            Complemento: customerData.AddressComplement || '',
            Bairro: customerData.Suburb,
            Cidade: customerData.City,
            UF: customerData.State,
            CEP: customerData.PostCode
          },
          Email: customerData.Email,
          TelefoneSMS: customerData.Mobile,
          PoliticamenteExposta: false // Valor padrão conforme exemplo
        };
        
        console.log('Dados do segurado preparados:', seguradoData);
      } else {
        console.log('Tipo DEALER: dados do segurado não serão enviados');
      }
      
      // Chamar o serviço para atualizar a data de efetivação final
      const updateResponse = await vehicleService.updateFinalEffectiveDate(
        transaction.plateNo,
        effectiveDate,
        seguradoData
      );
      
      console.log('Atualização da data de efetivação:', updateResponse);
      
      // Chamar o serviço para emitir no PMS
      const emitResponse = await vehicleService.emitInPMS(transaction.id);

      console.log('Emissão no PMS:', emitResponse);

      // Backend retorna 200 mesmo em falha — checar status no body
      if (emitResponse.status === 'Problema de Emissão') {
        const pmsMessage = emitResponse.emissions?.[0]?.pmsMessage;
        throw new Error(pmsMessage || 'Não foi possível emitir a apólice. Verifique os dados e tente novamente.');
      }

      // Atualizar a transação na lista com os novos dados
      setTransactions(prev => 
        prev.map(item => 
          item.id === emitResponse.id 
            ? { 
                ...item, 
                finalEffectiveDate: updateResponse.finalEffectiveDate,
                status: emitResponse.status,
                policyNo: emitResponse.policyNo,
                customerName: emitResponse.customerName || item.customerName,
                idNo: emitResponse.idNo || item.idNo,
                contractNo: emitResponse.contractNo || item.contractNo
              } 
            : item
        )
      );
      
      // Armazenar informações de sucesso
      setSuccessInfo({
        transactionId: transaction.id,
        policyNo: emitResponse.policyNo,
        customerName: emitResponse.customerName,
        idNo: emitResponse.idNo,
        contractNo: emitResponse.contractNo,
        emissions: emitResponse.emissions
      });
      
      // Mudar para o step de sucesso
      setCurrentStep('success');
      
      // Limpar os campos após o envio
      setSelectedFile(null);
      setEffectiveDate('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao enviar documentos:', error);
      toaster.create({
        type: 'error',
        title: 'Não foi possível emitir a apólice',
        description: error instanceof Error ? error.message : 'Não foi possível processar a emissão. Verifique os documentos e tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para processar o upgrade do plano
  const handleUpgradePlan = async (): Promise<{ success: boolean; transaction?: Transaction }> => {
    if (!selectedUpgradePlan) {
      setUpgradeMessage({
        type: 'info',
        message: 'Por favor, selecione uma das opções acima para continuar'
      });
      return { success: false };
    }
    
    const transaction = transactions?.[0];

    // Se selecionou "no-upgrade", pode precisar desfazer um upgrade já aplicado no backend
    if (selectedUpgradePlan === 'no-upgrade') {
      if (
        transaction &&
        lastUpgradeRef.current.applied &&
        String(lastUpgradeRef.current.contractId) === String(transaction.id)
      ) {
        try {
          setIsUpgrading(true);
          setUpgradeMessage(null);

          const downgradedTransaction = await vehicleService.downgradePlan(
            String(transaction.id),
            lastUpgradeRef.current.fromPlanId
          );

          const mergedTransaction = {
            ...transaction,
            ...downgradedTransaction,
            upgradeablePlans: transaction.upgradeablePlans ?? []
          };

          setTransactions([mergedTransaction]);
          lastUpgradeRef.current = { applied: false };

          setUpgradeMessage({
            type: 'info',
            message: 'Ok! Upgrade desfeito e você pode prosseguir sem upgrade.'
          });

          if (mergedTransaction.planInsuranceHolder === "CUSTOMER") {
            setCurrentStep('customer');
          } else {
            setCurrentStep('effectivation');
          }

          return { success: true, transaction: mergedTransaction };
        } catch (error) {
          console.error('Erro ao desfazer upgrade (downgrade):', error);
          toaster.create({ type: 'error', title: error instanceof Error ? error.message : 'Não foi possível desfazer o upgrade. Por favor, tente novamente.' });
          return { success: false };
        } finally {
          setIsUpgrading(false);
        }
      }

      setUpgradeMessage({
        type: 'info',
        message: 'Perfeito! Você pode prosseguir com a efetivação da apólice mantendo o plano atual.'
      });

      if (transaction?.planInsuranceHolder === "CUSTOMER") {
        setCurrentStep('customer');
      } else {
        setCurrentStep('effectivation');
      }

      return { success: true, transaction };
    }
    
    if (!transaction) {
      toaster.create({ type: 'error', title: 'Nenhuma transação encontrada' });
      return { success: false };
    }
    
    try {
      setIsUpgrading(true);
      setUpgradeMessage(null);

      // Se já houve upgrade e o usuário escolheu outro plano, primeiro desfaz no backend
      let baseTransaction: Transaction = transaction;
      if (
        lastUpgradeRef.current.applied &&
        String(lastUpgradeRef.current.contractId) === String(transaction.id) &&
        lastUpgradeRef.current.toPlanId &&
        lastUpgradeRef.current.toPlanId !== selectedUpgradePlan
      ) {
        const downgradedTransaction = await vehicleService.downgradePlan(
          String(transaction.id),
          lastUpgradeRef.current.fromPlanId
        );

        baseTransaction = {
          ...transaction,
          ...downgradedTransaction,
          upgradeablePlans: transaction.upgradeablePlans ?? []
        };

        setTransactions([baseTransaction]);
        lastUpgradeRef.current = { applied: false };
      }
      
      // Salvar dados críticos antes do upgrade
      const originalPlanInsuranceHolder = baseTransaction.planInsuranceHolder;
      const originalStatus = baseTransaction.status;
      
      console.log('=== ANTES DO UPGRADE ===');
      console.log('planInsuranceHolder original:', originalPlanInsuranceHolder);
      console.log('status original:', originalStatus);
      
      // Encontrar o plano selecionado para obter o código correto
      const selectedPlan = baseTransaction.upgradeablePlans?.find(p => p.id === selectedUpgradePlan);
      const planCode = selectedPlan?.plan || selectedUpgradePlan;
      
      // Obter informações do plano para mensagem personalizada
      const planInfo = getUpgradePlanInfo(planCode);
      
      // Chamar o serviço de upgrade
      const updatedTransaction = await vehicleService.upgradePlan(
        String(transaction.id),
        selectedUpgradePlan
      );
      
      console.log('=== RESPOSTA DO BACKEND ===');
      console.log('Transação atualizada completa:', updatedTransaction);
      console.log('planInsuranceHolder retornado:', updatedTransaction.planInsuranceHolder);
      console.log('status retornado:', updatedTransaction.status);
      
      // Atualizar a transação com os novos dados, garantindo que campos críticos sejam preservados
      const mergedTransaction = {
        ...baseTransaction, // Base: transação original (ou já revertida via downgrade)
        ...updatedTransaction, // Sobrescrever com dados atualizados do backend
        // Forçar preservação dos campos críticos se o backend não os retornou ou retornou vazios
        planInsuranceHolder: updatedTransaction.planInsuranceHolder || originalPlanInsuranceHolder,
        status: updatedTransaction.status || originalStatus,
        // Manter upgradeablePlans para permitir voltar à tela de Planos e trocar de opção
        upgradeablePlans: baseTransaction.upgradeablePlans ?? []
      };
      
      console.log('=== APÓS MERGE ===');
      console.log('Transação mesclada:', mergedTransaction);
      console.log('planInsuranceHolder final:', mergedTransaction.planInsuranceHolder);
      console.log('status final:', mergedTransaction.status);
      
      setTransactions([mergedTransaction]);

      // Registrar que houve upgrade aplicado (para permitir downgrade se o usuário desistir)
      lastUpgradeRef.current = {
        applied: true,
        contractId: mergedTransaction.id,
        fromPlanId: baseTransaction.planId,
        fromPlanCode: baseTransaction.planCode,
        toPlanId: selectedUpgradePlan
      };
      
      setUpgradeMessage({
        type: 'success',
        message: `Parabéns! Upgrade realizado com sucesso! Sua proteção foi estendida para ${planInfo.duration}. Você fez uma excelente escolha garantindo mais tranquilidade e segurança!`
      });
      
      // Navegar automaticamente após o upgrade
      setTimeout(() => {
        console.log('=== NAVEGAÇÃO AUTOMÁTICA APÓS UPGRADE ===');
        console.log('Transação para navegação:', mergedTransaction);
        console.log('planInsuranceHolder:', mergedTransaction.planInsuranceHolder);
        
        // Navegar baseado na transação atualizada
        if (mergedTransaction.planInsuranceHolder === "CUSTOMER") {
          console.log('✓ Navegando para CUSTOMER (formulário de dados)');
          setCurrentStep('customer');
        } else {
          console.log('✓ Navegando para EFFECTIVATION (sem formulário)');
          setCurrentStep('effectivation');
        }
      }, 1500);
      
      // Retornar a transação atualizada para navegação correta
      return { success: true, transaction: mergedTransaction };
    } catch (error) {
      console.error('Erro ao fazer upgrade:', error);
      toaster.create({ type: 'error', title: error instanceof Error ? error.message : 'Não foi possível processar o upgrade. Por favor, tente novamente.' });
      return { success: false };
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      toaster.create({ type: 'warning', title: 'Digite uma placa ou chassi para realizar a busca' });
      return;
    }

    setIsLoading(true);
    // Resetar o upgrade
    setSelectedUpgradePlan('');
    setUpgradeMessage(null);

    try {
      const result = await vehicleService.searchVehicle(searchValue);
      setTransactions(result);
      
      if (!result || result.length === 0) {
        toaster.create({ type: 'info', title: 'Não foram encontrados veículos com os dados informados' });
        setCurrentStep('search');
      } else {
        // Avançar para a próxima etapa após busca bem-sucedida
        setCurrentStep('details');
      }
    } catch (error) {
      toaster.create({ type: 'error', title: 'Nenhum veículo encontrado, por gentileza, entre em contato com a Auto Avaliar' });
      setCurrentStep('search');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Função para navegar entre steps
  const handleContinueToUpgrade = () => {
    const transaction = transactions?.[0];
    if (!UPGRADE_ENABLED) {
      // Quando upgrade estiver desativado, o usuário segue direto para preencher os dados.
      handleContinueToCustomer();
      return;
    }
    if (transaction?.upgradeablePlans && transaction.upgradeablePlans.length > 0) {
      setCurrentStep('upgrade');
    } else {
      handleContinueToCustomer();
    }
  };
  
  const handleContinueToCustomer = () => {
    const transaction = transactions?.[0];
    console.log('=== handleContinueToCustomer ===');
    console.log('Transaction completa:', transaction);
    console.log('planInsuranceHolder:', transaction?.planInsuranceHolder);
    console.log('Tipo de planInsuranceHolder:', typeof transaction?.planInsuranceHolder);
    console.log('status:', transaction?.status);
    console.log('Transactions array:', transactions);
    
    if (transaction?.planInsuranceHolder === "CUSTOMER") {
      console.log('✓ Indo para step customer (CUSTOMER detectado)');
      setCurrentStep('customer');
    } else {
      console.log('✓ Indo para step effectivation');
      console.log('  - planInsuranceHolder:', transaction?.planInsuranceHolder);
      console.log('  - status:', transaction?.status);
      console.log('  - Status "Estoque"?', transaction?.status === "Estoque");
      setCurrentStep('effectivation');
    }
  };
  
  const handleContinueToEffectivation = () => {
    setCurrentStep('effectivation');
  };
  
  const handleBackToDetails = () => {
    setCurrentStep('details');
  };
  
  const handleBackToUpgrade = () => {
    if (!UPGRADE_ENABLED) {
      setCurrentStep('details');
      return;
    }
    setCurrentStep('upgrade');
  };
  
  const handleBackToCustomer = () => {
    setCurrentStep('customer');
  };
  
  const handleNewSearch = () => {
    setSearchValue('');
    setTransactions([]);
    setCurrentStep('search');
    setSelectedUpgradePlan('');
    setUpgradeMessage(null);
    setSelectedFile(null);
    setEffectiveDate('');
    setSuccessInfo(null);
    formik.resetForm();
  };


  return (
    <Box 
      bg="#F7F8FA" 
      minH="100vh" 
      display="flex" 
      flexDirection="column"
    >
      <Header title="Sistema de Efetivação de Apólices" subtitle="Consulta de Veículos" />
      
      {/* Conteúdo principal - Layout Wizard */}
      <Box flex="1" display="flex" alignItems="center" justifyContent="center" py={3} px={4}>
        <Box 
          maxW={{ base: "95%", md: "1200px", lg: "1400px", xl: "1600px" }}
          w="100%"
          minH="450px"
        >
          {/* Progress Indicator */}
          {currentStep !== 'search' && (
            <Flex mb={4} gap={2} justifyContent="center" alignItems="center">
              <Flex alignItems="center" gap={2}>
                <Box 
                  w="10px" 
                  h="10px" 
                  borderRadius="full" 
                  bg="#10b981"
                />
                <Text fontSize="xs" fontWeight="600" color="gray.700">Buscar</Text>
              </Flex>
              
              <Box w="40px" h="2px" bg={currentStep !== 'details' ? "#10b981" : "gray.300"} />
              
              <Flex alignItems="center" gap={2}>
                <Box 
                  w="10px" 
                  h="10px" 
                  borderRadius="full" 
                  bg={currentStep === 'details' ? "#3b82f6" : (currentStep === 'upgrade' || currentStep === 'customer' || currentStep === 'effectivation') ? "#10b981" : "gray.300"}
                />
                <Text fontSize="xs" fontWeight="600" color={currentStep === 'details' ? "#3b82f6" : "gray.700"}>Detalhes</Text>
              </Flex>
              
              {transactions?.[0]?.upgradeablePlans && transactions[0].upgradeablePlans.length > 0 && (
                <>
                  <Box w="40px" h="2px" bg={(currentStep === 'customer' || currentStep === 'effectivation') ? "#10b981" : "gray.300"} />
                  <Flex alignItems="center" gap={2}>
                    <Box 
                      w="10px" 
                      h="10px" 
                      borderRadius="full" 
                      bg={currentStep === 'upgrade' ? "#3b82f6" : (currentStep === 'customer' || currentStep === 'effectivation') ? "#10b981" : "gray.300"}
                    />
                    <Text fontSize="xs" fontWeight="600" color={currentStep === 'upgrade' ? "#3b82f6" : "gray.700"}>Planos</Text>
                  </Flex>
                </>
              )}
              
              {transactions?.[0]?.planInsuranceHolder === "CUSTOMER" && (
                <>
                  <Box w="40px" h="2px" bg={currentStep === 'effectivation' ? "#10b981" : "gray.300"} />
                  <Flex alignItems="center" gap={2}>
                    <Box 
                      w="10px" 
                      h="10px" 
                      borderRadius="full" 
                      bg={currentStep === 'customer' ? "#3b82f6" : currentStep === 'effectivation' ? "#10b981" : "gray.300"}
                    />
                    <Text fontSize="xs" fontWeight="600" color={currentStep === 'customer' ? "#3b82f6" : "gray.700"}>Dados</Text>
                  </Flex>
                </>
              )}
              
              <Box w="40px" h="2px" bg={(currentStep === 'effectivation' || currentStep === 'success') ? "#10b981" : "gray.300"} />
              
              <Flex alignItems="center" gap={2}>
                <Box 
                  w="10px" 
                  h="10px" 
                  borderRadius="full" 
                  bg={currentStep === 'effectivation' ? "#3b82f6" : currentStep === 'success' ? "#10b981" : "gray.300"}
                />
                <Text fontSize="xs" fontWeight="600" color={currentStep === 'effectivation' ? "#3b82f6" : "gray.700"}>Contratação</Text>
              </Flex>
            </Flex>
          )}
          
          {/* Card Principal do Wizard */}
          <Box 
          bg="white"
            borderRadius="2xl"
          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
            overflow="hidden"
            minH="420px"
            display="flex"
            flexDirection="column"
        >
            <Box p={{ base: 5, md: 8, lg: 10 }} flex="1" display="flex" flexDirection="column">
              <VStack gap={5} alignItems="stretch" flex="1">
            {/* STEP 1: Formulário de busca */}
            {currentStep === 'search' && (
            <Box 
              p={{ base: 8, md: 10, lg: 12 }} 
              borderRadius="2xl" 
              bg="white"
              boxShadow="0 4px 20px rgba(0,0,0,0.08)"
              borderWidth="1px"
              borderColor="rgba(59, 130, 246, 0.1)"
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                bg: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
              }}
            >
              <VStack gap={6} alignItems="stretch">
                {/* Título e descrição */}
                <Box textAlign="center" mb={2}>
                  <Heading 
                    size="lg" 
                    fontWeight="700" 
                    color="#2D3748"
                    letterSpacing="tight"
                    mb={2}
                  >
                    Consulta de Veículos
                  </Heading>
                  <Text 
                    fontSize="sm" 
                    color="gray.600"
                    fontWeight="500"
                  >
                    Digite a placa ou chassi do veículo para iniciar a contratação
                  </Text>
                </Box>

                {/* Campo de busca */}
                <Box width="100%">
                  <Text 
                    mb={3} 
                    fontWeight="600" 
                    fontSize="sm" 
                    color="gray.700"
                    letterSpacing="wide"
                    textTransform="uppercase"
                  >
                    Placa ou Chassi
                  </Text>
                  <Input
                    placeholder="Digite a placa ou chassi do veículo"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    borderColor="#E2E8F0"
                    size="lg"
                    borderRadius="xl"
                    fontSize="md"
                    h="52px"
                    bg="gray.50"
                    _hover={{ 
                      borderColor: "#3b82f6",
                      bg: "white"
                    }}
                    _focus={{ 
                      borderColor: "#3b82f6", 
                      boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.1)",
                      bg: "white"
                    }}
                    transition="all 0.2s ease"
                  />
                </Box>

                {/* Botão de busca */}
                <Button
                  bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                  color="white"
                  width="full"
                  maxW="400px"
                  mx="auto"
                  size="lg"
                  h="52px"
                  borderRadius="xl"
                  onClick={handleSearch}
                  disabled={isLoading}
                  _hover={{ 
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 30px rgba(51, 51, 204, 0.35)"
                  }}
                  _active={{
                    transform: "translateY(0px)"
                  }}
                  boxShadow="0 6px 20px rgba(51, 51, 204, 0.25)"
                  fontWeight="600"
                  fontSize="md"
                  transition="all 0.2s ease"
                >
                  {isLoading ? "Buscando..." : "Buscar Veículo"}
                </Button>
              </VStack>
            </Box>
            )}

            {/* STEP 2: Detalhes do Veículo */}
            {currentStep === 'details' && transactions && transactions.length > 0 && (
              <Box flex="1" display="flex" flexDirection="column">
                <Heading size="md" color="#2D3748" fontWeight="700" letterSpacing="tight" mb={6}>
                  Detalhes do Veículo
                </Heading>
                
                <Box flex="1">
                  {transactions?.map((transaction) => (
                    <Grid 
                      key={transaction.id}
                      templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                      gap={6}
                    >
                      <Box>
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">Número do Ticket</Text>
                          <Text fontWeight="600" fontSize="md" color="gray.800">{transaction.ticketNo}</Text>
                        </Box>
                        
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">Nome do Segurado</Text>
                          <Text fontWeight="600" fontSize="md" color="gray.800">{transaction.customerName}</Text>
                        </Box>
                        
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">CPF/CNPJ</Text>
                          <Text fontWeight="600" fontSize="md" color="gray.800">{formatCpfCnpj(transaction.idNo)}</Text>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">Número do Chassi</Text>
                          <Text fontWeight="600" fontSize="md" color="gray.800">{transaction.chassisNo}</Text>
                        </Box>
                        
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">Placa</Text>
                          <Text fontWeight="600" fontSize="md" color="gray.800">{transaction.plateNo}</Text>
                        </Box>
                        
                        <Box mb={4}>
                          <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide" fontWeight="600">Status</Text>
                          <Box 
                            display="inline-flex"
                            alignItems="center"
                            bg={transaction.situacao === 'Emitido' ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"}
                            color="white"
                            px={4}
                            py={2}
                            borderRadius="xl"
                            fontSize="sm"
                            fontWeight="700"
                            boxShadow={transaction.situacao === 'Emitido' ? "0 4px 15px rgba(16, 185, 129, 0.3)" : "0 4px 15px rgba(59, 130, 246, 0.3)"}
                          >
                            {transaction.status}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                        </Box>
                        
                <Flex mt={4} justifyContent="space-between">
                            <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={handleNewSearch}
                    minW="140px"
                    h="40px"
                    fontSize="sm"
                    fontWeight="600"
                  >
                    Nova Busca
                  </Button>
                  
                  {/* Se a apólice já foi emitida (situacao === 'Emitido'), mostrar botão(ões) de download em vez de continuar.
                      status é sobre faturamento/cobrança (Estoque/Aguardando Faturamento/Faturado/Pago) e não indica, por si só,
                      se a apólice foi emitida — um veículo pode virar "Faturado" via fechamento de boleto sem nunca ter sido emitido. */}
                  {transactions[0]?.situacao === 'Emitido' ? (
                    <>
                      {transactions[0]?.emissions && transactions[0].emissions.length > 1 ? (
                        <>
                          {/* Há upgrade - mostrar 2 botões */}
                          <Flex gap={2}>
                            <Button
                              bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                              color="white"
                              minW="140px"
                              h="40px"
                              borderRadius="lg"
                              onClick={() => handleDownloadPolicy(transactions[0].id, transactions[0].emissions![0].id)}
                              disabled={downloadingEmissionId === transactions[0].emissions![0].id}
                              _hover={{ 
                                transform: "translateY(-2px)",
                                boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)"
                              }}
                              fontWeight="600"
                              fontSize="sm"
                            >
                              {downloadingEmissionId === transactions[0].emissions![0].id ? (
                                <Flex alignItems="center" gap={2}>
                                  <Spinner size="sm" />
                                  <Text>Baixando...</Text>
                                </Flex>
                              ) : (
                                "Baixar Apólice Normal"
                              )}
                            </Button>
                            <Button
                              bg="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                              color="white"
                              minW="140px"
                              h="40px"
                              borderRadius="lg"
                              onClick={() => handleDownloadPolicy(transactions[0].id, transactions[0].emissions![1].id)}
                              disabled={downloadingEmissionId === transactions[0].emissions![1].id}
                              _hover={{ 
                                transform: "translateY(-2px)",
                                boxShadow: "0 8px 20px rgba(139, 92, 246, 0.3)"
                              }}
                              fontWeight="600"
                              fontSize="sm"
                            >
                              {downloadingEmissionId === transactions[0].emissions![1].id ? (
                                <Flex alignItems="center" gap={2}>
                                  <Spinner size="sm" />
                                  <Text>Baixando...</Text>
                                </Flex>
                              ) : (
                                "Baixar Apólice Complementar"
                              )}
                            </Button>
                          </Flex>
                        </>
                      ) : (
                        <>
                          {/* Não há upgrade - mostrar apenas 1 botão */}
                          <Button
                            bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                            color="white"
                            minW="140px"
                            h="40px"
                            borderRadius="lg"
                            onClick={() => handleDownloadPolicy(transactions[0].id)}
                            disabled={isLoading}
                            _hover={{ 
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)"
                            }}
                            fontWeight="600"
                            fontSize="sm"
                          >
                            {isLoading ? (
                              <Flex alignItems="center" gap={2}>
                                <Spinner size="sm" />
                                <Text>Baixando...</Text>
                              </Flex>
                            ) : (
                              "Baixar Apólice"
                            )}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <Button
                      bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                      color="white"
                      minW="140px"
                      h="40px"
                      borderRadius="lg"
                      onClick={handleContinueToUpgrade}
                      _hover={{ 
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)"
                      }}
                      fontWeight="600"
                      fontSize="sm"
                    >
                      Continuar
                    </Button>
                  )}
                </Flex>
              </Box>
            )}
            
            {/* STEP 3: Upgrade de Plano */}
            {UPGRADE_ENABLED && currentStep === 'upgrade' && transactions && transactions.length > 0 && transactions[0]?.upgradeablePlans && transactions[0].upgradeablePlans.length > 0 && (
              <Box 
                borderRadius="2xl" 
                bg="white"
                boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(59, 130, 246, 0.1)"
                mt={6}
                transition="all 0.3s ease"
                _hover={{
                  boxShadow: "0 15px 50px rgba(0,0,0,0.12)"
                }}
              >
                <Box 
                  p={6} 
                  borderBottomWidth="1px" 
                  borderBottomColor="gray.100"
                  bg="linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)"
                >
                  <Flex alignItems="center" gap={3} mb={2}>
                    <Box>
                      <Heading size="md" color="#2D3748" fontWeight="700" letterSpacing="tight">Oferta Especial de Upgrade</Heading>
                      <Text fontSize="sm" color="gray.600" mt={1} fontWeight="500">
                        Aproveite para estender sua proteção com condições especiais!
                      </Text>
                </Box>
                  </Flex>
                      <Box 
                    mt={3}
                    p={3}
                    bg="rgba(59, 130, 246, 0.1)"
                        borderRadius="lg" 
                        borderLeft="4px solid"
                    borderLeftColor="#3b82f6"
                  >
                    <Text fontSize="xs" color="gray.700" fontWeight="600">
                      Dica: Planos mais longos oferecem melhor custo-benefício e mais tranquilidade
                    </Text>
                  </Box>
                </Box>
                <Box p={6}>
                  <VStack gap={5} alignItems="stretch">
                    {/* Mensagem de feedback dentro do card */}
                    {upgradeMessage && (
                      <Box 
                        p={5} 
                        borderRadius="xl" 
                        bg={upgradeMessage.type === 'success' ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'}
                        color={upgradeMessage.type === 'success' ? 'green.900' : 'blue.900'}
                        borderLeft="5px solid"
                        borderLeftColor={upgradeMessage.type === 'success' ? 'green.600' : 'blue.600'}
                        boxShadow="0 8px 20px rgba(0,0,0,0.08)"
                        fontWeight="600"
                      >
                        {upgradeMessage.message}
                      </Box>
                    )}
                    
                    {/* Opção para não fazer upgrade */}
                    <Box 
                      p={5}
                      borderRadius="xl"
                      borderWidth="2px"
                      borderColor={selectedUpgradePlan === 'no-upgrade' ? "#6B7280" : "#E2E8F0"}
                      bg={selectedUpgradePlan === 'no-upgrade' ? "linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)" : "white"}
                      cursor="pointer"
                      transition="all 0.2s ease"
                      _hover={{ 
                        borderColor: "#6B7280",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(107, 114, 128, 0.15)"
                      }}
                      onClick={() => setSelectedUpgradePlan('no-upgrade')}
                      boxShadow={selectedUpgradePlan === 'no-upgrade' ? "0 8px 20px rgba(107, 114, 128, 0.2)" : "0 2px 8px rgba(0,0,0,0.05)"}
                    >
                      <Flex alignItems="center" justifyContent="space-between">
                      <Flex alignItems="center">
                        <Box 
                            width="24px" 
                            height="24px" 
                          borderRadius="full" 
                          borderWidth="2px" 
                            borderColor={selectedUpgradePlan === 'no-upgrade' ? "#6B7280" : "#E2E8F0"}
                            mr={4}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                            bg={selectedUpgradePlan === 'no-upgrade' ? "#6B7280" : "white"}
                            transition="all 0.2s ease"
                        >
                          {selectedUpgradePlan === 'no-upgrade' && (
                            <Box width="10px" height="10px" borderRadius="full" bg="white" />
                          )}
                        </Box>
                          <Box>
                            <Text fontWeight="600" fontSize="md" color={selectedUpgradePlan === 'no-upgrade' ? "#6B7280" : "#2D3748"}>
                              Manter plano atual
                        </Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Continuar sem upgrade
                            </Text>
                          </Box>
                        </Flex>
                      </Flex>
                    </Box>
                    
                    {/* Opções de upgrade disponíveis */}
                    {transactions[0].upgradeablePlans
                      .filter(plan => plan.enabled && plan.plan)
                      .map((plan) => {
                        const planInfo = getUpgradePlanInfo(plan.plan || '');
                        return (
                          <Box 
                            key={plan.id}
                            p={6}
                            borderRadius="xl"
                            borderWidth="3px"
                            borderColor={selectedUpgradePlan === plan.id ? planInfo.color : "#E2E8F0"}
                            bg={selectedUpgradePlan === plan.id ? 
                              `linear-gradient(135deg, ${planInfo.color}15 0%, ${planInfo.color}25 100%)` : 
                              "white"}
                            cursor="pointer"
                            transition="all 0.3s ease"
                            _hover={{ 
                              borderColor: planInfo.color,
                              transform: "translateY(-4px)",
                              boxShadow: `0 12px 30px ${planInfo.color}40`
                            }}
                          onClick={() => setSelectedUpgradePlan(plan.id)}
                            boxShadow={selectedUpgradePlan === plan.id ? 
                              `0 12px 30px ${planInfo.color}40` : 
                              "0 4px 12px rgba(0,0,0,0.08)"}
                            position="relative"
                            overflow="hidden"
                          >
                            {/* Badge de destaque */}
                            <Box
                              position="absolute"
                              top={3}
                              right={3}
                              bg={planInfo.color}
                              color="white"
                              px={3}
                              py={1}
                              borderRadius="full" 
                              fontSize="xs"
                              fontWeight="700"
                              boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                            >
                              {planInfo.badge}
                            </Box>

                            <Flex alignItems="flex-start" mb={4}>
                              <Box 
                                width="28px" 
                                height="28px" 
                                borderRadius="full" 
                                borderWidth="3px" 
                                borderColor={selectedUpgradePlan === plan.id ? planInfo.color : "#E2E8F0"}
                                mr={4}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                                bg={selectedUpgradePlan === plan.id ? planInfo.color : "white"}
                                transition="all 0.2s ease"
                                flexShrink={0}
                            >
                              {selectedUpgradePlan === plan.id && (
                                  <Box width="12px" height="12px" borderRadius="full" bg="white" />
                              )}
                            </Box>
                              
                              <Box flex={1}>
                                <Text 
                                  fontWeight="700" 
                                  fontSize="lg" 
                                  color={selectedUpgradePlan === plan.id ? planInfo.color : "#2D3748"}
                                  mb={2}
                                  lineHeight="1.3"
                                >
                                  {planInfo.title}
                            </Text>
                                <Text 
                                  fontSize="sm" 
                                  color="gray.600" 
                                  mb={3}
                                  fontWeight="500"
                                >
                                  {planInfo.description}
                                </Text>
                        </Box>
                            </Flex>

                            {/* Lista de benefícios */}
                            <VStack alignItems="flex-start" gap={2} mt={3} pl={11}>
                              {planInfo.benefits.map((benefit, idx) => (
                                <Flex key={idx} alignItems="center" gap={2}>
                                  <Box 
                                    w="5px" 
                                    h="5px" 
                                    borderRadius="full" 
                                    bg={planInfo.color}
                                    flexShrink={0}
                                  />
                                  <Text 
                                    fontSize="sm" 
                                    color="gray.700"
                                    fontWeight="500"
                                  >
                                    {benefit}
                                  </Text>
                                </Flex>
                              ))}
                            </VStack>

                            {/* Destaque visual para o plano selecionado */}
                            {selectedUpgradePlan === plan.id && (
                              <Box
                                mt={4}
                                p={3}
                                bg={`${planInfo.color}20`}
                                borderRadius="lg"
                                textAlign="center"
                              >
                                <Text fontSize="sm" fontWeight="700" color={planInfo.color}>
                                  Plano Selecionado
                                </Text>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    
                    {/* Mensagem informativa (apenas quando plano com upgrade selecionado) */}
                    {selectedUpgradePlan && selectedUpgradePlan !== 'no-upgrade' && (
                      <Box
                        mt={4}
                        p={3}
                        bg="blue.50"
                        borderRadius="lg"
                        borderLeft="4px solid"
                        borderLeftColor="blue.500"
                      >
                        <Text fontSize="sm" color="blue.900" fontWeight="600">
                          Proteção garantida: ao confirmar, seu upgrade será aplicado imediatamente.
                        </Text>
                      </Box>
                    )}

                    {/* Ação principal + Voltar */}
                    <Flex mt={6} gap={3} alignItems="center" flexWrap="wrap">
                      <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={handleBackToDetails}
                        minW="120px"
                        h="48px"
                        fontSize="sm"
                        fontWeight="600"
                        borderRadius="lg"
                      >
                        Voltar
                      </Button>
                      <Button
                        flex="1"
                        minW="200px"
                        bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        color="white"
                        h="48px"
                        borderRadius="lg"
                        fontSize="md"
                        fontWeight="600"
                        boxShadow="0 4px 14px rgba(16, 185, 129, 0.25)"
                        _hover={{
                          boxShadow: "0 6px 20px rgba(16, 185, 129, 0.35)",
                          transform: "translateY(-1px)"
                        }}
                        _active={{ transform: "translateY(0)" }}
                        onClick={handleUpgradePlan}
                        disabled={isUpgrading || !selectedUpgradePlan}
                        opacity={!selectedUpgradePlan ? 0.5 : 1}
                        cursor={!selectedUpgradePlan ? "not-allowed" : "pointer"}
                        transition="all 0.2s ease"
                      >
                        {isUpgrading ? (
                          <>
                            <Spinner size="sm" color="white" mr={2} />
                            Processando...
                          </>
                        ) : (
                          selectedUpgradePlan === 'no-upgrade' ? 'Continuar sem upgrade' : 'Confirmar upgrade'
                        )}
                      </Button>
                    </Flex>
                  </VStack>
                </Box>
              </Box>
            )}
            
            {/* STEP 4: Dados do Comprador */}
            {currentStep === 'customer' && transactions && transactions.length > 0 && (() => {
              console.log('=== RENDERIZANDO FORMULÁRIO DE DADOS ===');
              console.log('currentStep:', currentStep);
              console.log('transactions:', transactions);
              console.log('planInsuranceHolder atual:', transactions[0]?.planInsuranceHolder);
              return true;
            })() && (
              <Box 
                borderRadius="2xl" 
                bg="white"
                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(59, 130, 246, 0.1)"
              >
                <Box 
                  p={4} 
                  borderBottomWidth="1px" 
                  borderBottomColor="gray.100"
                  bg="linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)"
                >
                  <Heading size="sm" color="#2D3748" fontWeight="700" letterSpacing="tight">Dados do Comprador</Heading>
                  <Text fontSize="xs" color="gray.600" mt={1} fontWeight="500">Todos os campos são obrigatórios para efetivação da apólice</Text>
                </Box>
                <Box p={4}>
                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                    {/* Tipo de Cliente */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.CustomerType} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Tipo de Cliente</FormLabel>
                        <select 
                          name="CustomerType" 
                          value={customerData.CustomerType || ''}
                          onChange={handleCustomerDataChange}
                          style={{
                            borderColor: fieldErrors.CustomerType ? "#EF4444" : "#E2E8F0",
                            borderWidth: "1px",
                            borderRadius: "0.5rem",
                            padding: "0.5rem",
                            width: "100%",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            backgroundColor: "#F9FAFB",
                            transition: "all 0.2s ease"
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="PF">Pessoa Física</option>
                          <option value="PJ">Pessoa Jurídica</option>
                        </select>
                        {fieldErrors.CustomerType && (
                          <FormErrorMessage>{fieldErrors.CustomerType}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Nome do Cliente */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.CustomerName} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Nome do Cliente</FormLabel>
                        <Input 
                          name="CustomerName" 
                          value={customerData.CustomerName || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("CustomerName", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.CustomerName ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.CustomerName ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.CustomerName ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.CustomerName ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.CustomerName && (
                          <FormErrorMessage>{fieldErrors.CustomerName}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Data de Nascimento */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.DateOfBirth} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Data de Nascimento</FormLabel>
                        <Input 
                          type="date" 
                          name="DateOfBirth" 
                          value={customerData.DateOfBirth || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("DateOfBirth", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.DateOfBirth ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.DateOfBirth ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.DateOfBirth ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.DateOfBirth ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.DateOfBirth && (
                          <FormErrorMessage>{fieldErrors.DateOfBirth}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Número do Documento */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.IdNo} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">CPF/CNPJ</FormLabel>
                        <Input 
                          name="IdNo" 
                          value={customerData.IdNo || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("IdNo", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.IdNo ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.IdNo ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.IdNo ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.IdNo ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.IdNo && (
                          <FormErrorMessage>{fieldErrors.IdNo}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Número de Documento */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.NumeroDocumento}>
                        <Text fontSize="xs" color="gray.600" mb={2} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Número de Documento</Text>
                        <Input 
                          name="NumeroDocumento" 
                          value={customerData.NumeroDocumento || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("NumeroDocumento", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.NumeroDocumento ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.NumeroDocumento ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.NumeroDocumento ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.NumeroDocumento ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                          placeholder="Opcional"
                        />
                        {fieldErrors.NumeroDocumento && (
                          <FormErrorMessage>{fieldErrors.NumeroDocumento}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Órgão Expedidor */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.OrgaoExpedidor}>
                        <Text fontSize="xs" color="gray.600" mb={2} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Órgão Expedidor <Text as="span" fontWeight="400" textTransform="none" color="gray.400">(Opcional)</Text></Text>
                        <Input 
                          name="OrgaoExpedidor" 
                          value={customerData.OrgaoExpedidor || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("OrgaoExpedidor", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.OrgaoExpedidor ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.OrgaoExpedidor ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.OrgaoExpedidor ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.OrgaoExpedidor ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                          placeholder="Opcional"
                        />
                        {fieldErrors.OrgaoExpedidor && (
                          <FormErrorMessage>{fieldErrors.OrgaoExpedidor}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Gênero */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.Gender} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Gênero</FormLabel>
                        <select 
                          name="Gender" 
                          value={customerData.Gender || ''} 
                          onChange={handleCustomerDataChange}
                          style={{
                            borderColor: fieldErrors.Gender ? "#EF4444" : "#E2E8F0",
                            borderWidth: "1px",
                            borderRadius: "0.5rem",
                            padding: "0.5rem",
                            width: "100%",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            backgroundColor: "#F9FAFB",
                            transition: "all 0.2s ease"
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="1">Masculino</option>
                          <option value="2">Feminino</option>
                          <option value="0">Outro</option>
                        </select>
                        {fieldErrors.Gender && (
                          <FormErrorMessage>{fieldErrors.Gender}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Nacionalidade */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.Nationality} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Nacionalidade</FormLabel>
                        <Input 
                          name="Nationality" 
                          value={customerData.Nationality || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("Nationality", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.Nationality ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.Nationality ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.Nationality ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.Nationality ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.Nationality && (
                          <FormErrorMessage>{fieldErrors.Nationality}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Estado Civil */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.EstadoCivil} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Estado Civil</FormLabel>
                        <select 
                          name="EstadoCivil" 
                          value={customerData.EstadoCivil || ''} 
                          onChange={handleCustomerDataChange}
                          style={{
                            borderColor: fieldErrors.EstadoCivil ? "#EF4444" : "#E2E8F0",
                            borderWidth: "1px",
                            borderRadius: "0.5rem",
                            padding: "0.5rem",
                            width: "100%",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            backgroundColor: "#F9FAFB",
                            transition: "all 0.2s ease"
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="0">Não Informado</option>
                          <option value="1">Solteiro(a)</option>
                          <option value="2">Casado(a)</option>
                          <option value="3">Divorciado(a)</option>
                          <option value="4">Divorciado(a) Judicialmente</option>
                          <option value="5">Viúvo(a)</option>
                          <option value="6">Outros</option>
                        </select>
                        {fieldErrors.EstadoCivil && (
                          <FormErrorMessage>{fieldErrors.EstadoCivil || ''}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Telefone Celular */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.Mobile} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Telefone Celular</FormLabel>
                        <Input 
                          name="Mobile" 
                          value={customerData.Mobile || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("Mobile", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.Mobile ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.Mobile ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.Mobile ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.Mobile ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                          placeholder="Somente números"
                        />
                        {fieldErrors.Mobile && (
                          <FormErrorMessage>{fieldErrors.Mobile}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Email */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.Email} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Email</FormLabel>
                        <Input 
                          type="email" 
                          name="Email" 
                          value={customerData.Email || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("Email", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.Email ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.Email ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.Email ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.Email ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.Email && (
                          <FormErrorMessage>{fieldErrors.Email}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* CEP */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.PostCode} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">CEP</FormLabel>
                        <Input 
                          name="PostCode" 
                          value={customerData.PostCode || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("PostCode", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.PostCode ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.PostCode ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.PostCode ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.PostCode ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                          placeholder="Somente números"
                          maxLength={8}
                        />
                        {fieldErrors.PostCode && (
                          <FormErrorMessage>{fieldErrors.PostCode}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Endereço */}
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl isInvalid={!!fieldErrors.Address} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Endereço</FormLabel>
                        <Input 
                          name="Address" 
                          value={customerData.Address || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("Address", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.Address ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.Address ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.Address ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.Address ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.Address && (
                          <FormErrorMessage>{fieldErrors.Address}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Número */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.AddressNumber} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Número</FormLabel>
                        <Input 
                          name="AddressNumber" 
                          value={customerData.AddressNumber || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("AddressNumber", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.AddressNumber ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.AddressNumber ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.AddressNumber ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.AddressNumber ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.AddressNumber && (
                          <FormErrorMessage>{fieldErrors.AddressNumber}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Complemento */}
                    <GridItem>
                      <Text fontSize="sm" color="gray.600" mb={1}>Complemento</Text>
                      <Input 
                        name="AddressComplement" 
                        value={customerData.AddressComplement || ''} 
                        onChange={handleCustomerDataChange}
                        borderColor="#E2E8F0"
                        _hover={{ borderColor: "#3333CC" }}
                        _focus={{ borderColor: "#3333CC", boxShadow: "0 0 0 1px #3333CC" }}
                      />
                    </GridItem>
                    
                    {/* Bairro */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.Suburb} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Bairro</FormLabel>
                        <Input 
                          name="Suburb" 
                          value={customerData.Suburb || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("Suburb", e.target.value);
                            }, 0);
                          }}
                          borderColor={fieldErrors.Suburb ? "#EF4444" : "#E2E8F0"}
                          borderRadius="lg"
                          h="36px"
                          bg="gray.50"
                          fontSize="0.875rem"
                          fontWeight="500"
                          _hover={{ 
                            borderColor: fieldErrors.Suburb ? "#EF4444" : "#3b82f6",
                            bg: "white"
                          }}
                          _focus={{ 
                            borderColor: fieldErrors.Suburb ? "#EF4444" : "#3b82f6", 
                            boxShadow: fieldErrors.Suburb ? "0 0 0 3px rgba(239, 68, 68, 0.1)" : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            bg: "white"
                          }}
                          transition="all 0.2s ease"
                        />
                        {fieldErrors.Suburb && (
                          <FormErrorMessage>{fieldErrors.Suburb}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Estado */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.State} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Estado</FormLabel>
                        <select 
                          name="State" 
                          value={customerData.State || ''} 
                          onChange={handleCustomerDataChange}
                          onBlur={(e) => {
                            setTimeout(() => {
                              validateField("State", e.target.value);
                            }, 0);
                          }}
                          style={{
                            borderColor: fieldErrors.State ? "#E53E3E" : "#E2E8F0",
                            borderWidth: "1px",
                            borderRadius: "0.375rem",
                            padding: "0.5rem",
                            width: "100%"
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="AC">Acre</option>
                          <option value="AL">Alagoas</option>
                          <option value="AP">Amapá</option>
                          <option value="AM">Amazonas</option>
                          <option value="BA">Bahia</option>
                          <option value="CE">Ceará</option>
                          <option value="DF">Distrito Federal</option>
                          <option value="ES">Espírito Santo</option>
                          <option value="GO">Goiás</option>
                          <option value="MA">Maranhão</option>
                          <option value="MT">Mato Grosso</option>
                          <option value="MS">Mato Grosso do Sul</option>
                          <option value="MG">Minas Gerais</option>
                          <option value="PA">Pará</option>
                          <option value="PB">Paraíba</option>
                          <option value="PR">Paraná</option>
                          <option value="PE">Pernambuco</option>
                          <option value="PI">Piauí</option>
                          <option value="RJ">Rio de Janeiro</option>
                          <option value="RN">Rio Grande do Norte</option>
                          <option value="RS">Rio Grande do Sul</option>
                          <option value="RO">Rondônia</option>
                          <option value="RR">Roraima</option>
                          <option value="SC">Santa Catarina</option>
                          <option value="SP">São Paulo</option>
                          <option value="SE">Sergipe</option>
                          <option value="TO">Tocantins</option>
                        </select>
                        {fieldErrors.State && (
                          <FormErrorMessage>{fieldErrors.State}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                    
                    {/* Cidade */}
                    <GridItem>
                      <FormControl isInvalid={!!fieldErrors.City} isRequired>
                        <FormLabel fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Cidade</FormLabel>
                        {loadingCities ? (
                          <Flex align="center">
                            <Spinner size="sm" color="#3333CC" mr={2} />
                            <Text fontSize="sm">Carregando cidades...</Text>
                          </Flex>
                        ) : (
                          <select 
                            name="City" 
                            value={customerData.City || ''} 
                            onChange={handleCustomerDataChange}
                            onBlur={(e) => {
                              setTimeout(() => {
                                validateField("City", e.target.value);
                              }, 0);
                            }}
                            style={{
                              borderColor: fieldErrors.City ? "#E53E3E" : "#E2E8F0",
                              borderWidth: "1px",
                              borderRadius: "0.375rem",
                              padding: "0.5rem",
                              width: "100%"
                            }}
                            disabled={!customerData.State || !cities || cities.length === 0}
                          >
                            <option value="">Selecione uma cidade</option>
                            {cities?.map((city) => (
                              <option key={city.ibgeCity} value={city.name}>
                                {city.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {fieldErrors.City && (
                          <FormErrorMessage>{fieldErrors.City}</FormErrorMessage>
                        )}
                      </FormControl>
                    </GridItem>
                  </Grid>
                  
                  {/* Botões de navegação */}
                  <Flex mt={4} justifyContent="space-between">
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      onClick={() => {
                        const transaction = transactions?.[0];
                        if (transaction?.upgradeablePlans && transaction.upgradeablePlans.length > 0) {
                          handleBackToUpgrade();
                        } else {
                          handleBackToDetails();
                        }
                      }}
                      minW="140px"
                      h="40px"
                      fontSize="sm"
                      fontWeight="600"
                    >
                      Voltar
                    </Button>
                    <Button
                      bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                      color="white"
                      minW="140px"
                      h="40px"
                      borderRadius="lg"
                      onClick={handleContinueToEffectivation}
                      disabled={!isCustomerDataComplete()}
                      opacity={!isCustomerDataComplete() ? 0.6 : 1}
                      _hover={{ 
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)"
                      }}
                      fontWeight="600"
                      fontSize="sm"
                    >
                      Continuar
                    </Button>
                  </Flex>
                </Box>
              </Box>
            )}
            
            {/* STEP 5: Efetivação da Apólice */}
            {currentStep === 'effectivation' && transactions && transactions.length > 0 && (
              transactions[0]?.situacao !== 'Emitido' ? (
              <Box 
                borderRadius="2xl" 
                bg="white"
                boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(59, 130, 246, 0.1)"
                mt={6}
                transition="all 0.3s ease"
                _hover={{
                  boxShadow: "0 15px 50px rgba(0,0,0,0.12)"
                }}
              >
                <Box 
                  p={6} 
                  borderBottomWidth="1px" 
                  borderBottomColor="gray.100"
                  bg="linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)"
                >
                  <Heading size="md" color="#2D3748" fontWeight="700" letterSpacing="tight">Contratação do Seguro</Heading>
                </Box>
                <Box p={6}>
                  <VStack gap={6} alignItems="stretch">
                    {/* Input para upload de arquivo PDF */}
                    <Box>
                      <Text fontWeight="600" mb={3} color="gray.700" fontSize="sm">Nota Fiscal (PDF)</Text>
                      <InputGroup>
                        <Box
                          position="relative"
                          width="100%"
                          border="2px dashed"
                          borderColor={isDragOver ? "#2563eb" : selectedFile ? "#3b82f6" : "#E2E8F0"}
                          borderRadius="xl"
                          p={8}
                          textAlign="center"
                          transition="all 0.3s ease"
                          bg={isDragOver ? "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)" : selectedFile ? "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)" : "gray.50"}
                          boxShadow={isDragOver ? "0 8px 24px rgba(59, 130, 246, 0.2)" : undefined}
                          _hover={{
                            borderColor: "#3b82f6",
                            cursor: "pointer",
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 20px rgba(59, 130, 246, 0.15)"
                          }}
                          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                          onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
                          onDragLeave={() => setIsDragOver(false)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDragOver(false);
                            const file = e.dataTransfer.files?.[0] || null;
                            if (file) {
                              const fakeEvent = { target: { files: e.dataTransfer.files, value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>;
                              handleFileChange(fakeEvent);
                            }
                          }}
                        >
                          <Input
                            type="file"
                            height="100%"
                            width="100%"
                            position="absolute"
                            top="0"
                            left="0"
                            opacity="0"
                            aria-hidden="true"
                            accept="application/pdf"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            cursor="pointer"
                          />
                          <Box py={3}>
                            <Text 
                              color={selectedFile ? "#3b82f6" : "gray.600"} 
                              mb={2}
                              fontWeight="600"
                              fontSize="md"
                            >
                              {selectedFile ? `${selectedFile.name}` : "Clique ou arraste para fazer upload"}
                            </Text>
                            <Text fontSize="sm" color="gray.500" fontWeight="500">
                              {selectedFile ? `Tamanho: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : "Somente arquivos PDF são aceitos"}
                            </Text>
                          </Box>
                        </Box>
                      </InputGroup>
                      <Text fontSize="xs" color="gray.500" mt={2} fontWeight="500">O arquivo deve estar no formato PDF</Text>
                    </Box>
                    
                    {/* Input para data de efetivação */}
                    <Box maxW="25%" alignSelf="flex-start">
                      <Text fontWeight="600" mb={3} color="gray.700" fontSize="sm">Data de Início de Vigência</Text>
                      <Input
                        type="date"
                        value={effectiveDate}
                        onChange={handleDateChange}
                        borderColor="#E2E8F0"
                        borderRadius="xl"
                        h="56px"
                        bg="gray.50"
                        fontSize="0.95rem"
                        fontWeight="500"
                        _hover={{ 
                          borderColor: "#3b82f6",
                          bg: "white"
                        }}
                        _focus={{ 
                          borderColor: "#3b82f6", 
                          boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          bg: "white"
                        }}
                        transition="all 0.2s ease"
                      />
                      <Text fontSize="xs" color="gray.500" mt={2} fontWeight="500">Informe a data de Início de Vigência do seguro</Text>
                    </Box>
                    
                    {/* Botões de navegação e efetivação */}
                    <Flex mt={4} justifyContent="space-between">
                    <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={() => {
                          const transaction = transactions?.[0];
                          if (transaction?.planInsuranceHolder === "CUSTOMER") {
                            handleBackToCustomer();
                          } else if (transaction?.upgradeablePlans && transaction.upgradeablePlans.length > 0) {
                            handleBackToUpgrade();
                          } else {
                            handleBackToDetails();
                          }
                        }}
                        minW="140px"
                        h="40px"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Voltar
                      </Button>
                      <Button
                        bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      color="white"
                        minW="140px"
                        h="40px"
                        borderRadius="lg"
                        fontSize="sm"
                        fontWeight="600"
                        _hover={{ 
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)"
                        }}
                        _active={{
                          transform: "translateY(0px)"
                        }}
                      onClick={handleSubmitDocuments}
                        boxShadow="0 4px 12px rgba(16, 185, 129, 0.2)"
                      disabled={
                        isLoading || 
                        !selectedFile || 
                        !effectiveDate || 
                        (transactions?.[0]?.planInsuranceHolder === "CUSTOMER" && !isCustomerDataComplete())
                      }
                      opacity={
                        !selectedFile || 
                        !effectiveDate || 
                        (transactions?.[0]?.planInsuranceHolder === "CUSTOMER" && !isCustomerDataComplete()) ? 0.6 : 1
                      }
                        transition="all 0.2s ease"
                      >
                        {isLoading ? (
                          <>
                            <Spinner size="sm" color="white" mr={2} />
                            Processando...
                          </>
                        ) : (
                          "Emitir Apólice"
                        )}
                    </Button>
                    </Flex>
                  </VStack>
                </Box>
              </Box>
              ) : (
                /* Fallback quando o status não é "Estoque" */
                <Box 
                  p={8}
                  textAlign="center"
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                  borderWidth="1px"
                  borderColor="rgba(255, 193, 7, 0.3)"
                >
                  <Box
                    mb={4}
                    p={5}
                    bg="linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%)"
                    borderRadius="xl"
                    borderLeft="4px solid"
                    borderLeftColor="orange.500"
                  >
                    <Heading size="md" color="orange.700" mb={3}>
                      Status Atual: {transactions[0]?.status}
                    </Heading>
                    <Text fontSize="sm" color="gray.700" fontWeight="500">
                      Esta transação não está disponível para efetivação no momento. A apólice já foi emitida.
                    </Text>
                  </Box>
                  
                  <Flex gap={3} justifyContent="center">
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      onClick={() => {
                        const transaction = transactions?.[0];
                        if (transaction?.planInsuranceHolder === "CUSTOMER") {
                          handleBackToCustomer();
                        } else if (transaction?.upgradeablePlans && transaction.upgradeablePlans.length > 0) {
                          handleBackToUpgrade();
                        } else {
                          handleBackToDetails();
                        }
                      }}
                      minW="140px"
                      h="40px"
                      fontSize="sm"
                      fontWeight="600"
                    >
                      Voltar
                    </Button>
                    <Button
                      bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                      color="white"
                      minW="140px"
                      h="40px"
                      borderRadius="lg"
                      onClick={handleNewSearch}
                      _hover={{ 
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)"
                      }}
                      fontWeight="600"
                      fontSize="sm"
                    >
                      Nova Busca
                    </Button>
                  </Flex>
                </Box>
              )
            )}

            {/* STEP 6: Tela de Sucesso */}
            {currentStep === 'success' && successInfo && (
              <Box 
                borderRadius="2xl" 
                bg="white"
                boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                overflow="hidden"
                borderWidth="1px"
                borderColor="rgba(16, 185, 129, 0.2)"
                transition="all 0.3s ease"
              >
                <VStack gap={0} alignItems="stretch">
                  {/* Ícone de Sucesso */}
                  <Box 
                    bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    p={8}
                    textAlign="center"
                  >
                    <Flex justifyContent="center" mb={4}>
                      <Box
                        w="80px"
                        h="80px"
                        borderRadius="full"
                        bg="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxShadow="0 8px 30px rgba(0,0,0,0.15)"
                      >
                        <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
                          <path 
                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                            stroke="#10b981" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Box>
                    </Flex>
                    <Heading size="lg" color="white" fontWeight="700" mb={2}>
                      Apólice Emitida com Sucesso!
                    </Heading>
                  </Box>

                  {/* Informações da Apólice */}
                  <Box p={8}>
                    <VStack gap={6} alignItems="stretch">
                      {/* Número da Apólice */}
                      <Box 
                        p={6} 
                        bg="linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)"
                        borderRadius="xl"
                        borderWidth="2px"
                        borderColor="rgba(16, 185, 129, 0.3)"
                      >
                        <Text fontSize="sm" color="gray.600" fontWeight="600" mb={2}>
                          Número da Apólice
                        </Text>
                        <Text fontSize="2xl" fontWeight="800" color="#10b981" letterSpacing="tight">
                          {successInfo.policyNo}
                        </Text>
                      </Box>

                      {/* Detalhes Adicionais */}
                      {(successInfo.customerName || successInfo.idNo || successInfo.contractNo) && (
                        <Box 
                          p={6}
                          bg="gray.50"
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor="gray.200"
                        >
                          <Text fontSize="sm" fontWeight="700" color="gray.700" mb={4} textTransform="uppercase" letterSpacing="wide">
                            Detalhes do Contrato
                          </Text>
                          <VStack gap={3} alignItems="stretch">
                            {successInfo.customerName && (
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="sm" color="gray.600" fontWeight="500">Segurado:</Text>
                                <Text fontSize="sm" color="gray.900" fontWeight="700">{successInfo.customerName}</Text>
                              </Flex>
                            )}
                            {successInfo.idNo && (
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="sm" color="gray.600" fontWeight="500">CPF/CNPJ:</Text>
                                <Text fontSize="sm" color="gray.900" fontWeight="700">{successInfo.idNo}</Text>
                              </Flex>
                            )}
                            {successInfo.contractNo && (
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="sm" color="gray.600" fontWeight="500">Contrato:</Text>
                                <Text fontSize="sm" color="gray.900" fontWeight="700">{successInfo.contractNo}</Text>
                              </Flex>
                            )}
                          </VStack>
                        </Box>
                      )}

                      {/* Mensagem informativa sobre download */}
                      <Box
                        p={4}
                        bg="linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)"
                        borderRadius="xl"
                        borderLeft="4px solid"
                        borderLeftColor="blue.500"
                      >
                        <Flex alignItems="center" gap={2}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <Text fontSize="sm" color="blue.900" fontWeight="600">
                            Sua apólice está pronta para download em formato PDF
                          </Text>
                        </Flex>
                      </Box>

                      {/* Botões para Baixar Apólice */}
                      {successInfo.emissions && successInfo.emissions.length > 1 ? (
                        <>
                          {/* Há upgrade - mostrar 2 botões */}
                          <VStack gap={3} width="100%">
                            <Button
                              bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                              color="white"
                              h="56px"
                              width="100%"
                              borderRadius="xl"
                              fontSize="md"
                              fontWeight="700"
                              onClick={() => handleDownloadPolicy(successInfo.transactionId, successInfo.emissions![0].id)}
                              disabled={downloadingEmissionId === successInfo.emissions![0].id}
                              _hover={{ 
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 30px rgba(16, 185, 129, 0.35)"
                              }}
                              _active={{
                                transform: "translateY(0px)"
                              }}
                              boxShadow="0 6px 20px rgba(16, 185, 129, 0.25)"
                              transition="all 0.2s ease"
                            >
                              <Flex alignItems="center" gap={2}>
                                {downloadingEmissionId === successInfo.emissions![0].id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                                <Text>{downloadingEmissionId === successInfo.emissions![0].id ? "Baixando..." : `Baixar Apólice Normal (${successInfo.emissions![0].policyNo})`}</Text>
                              </Flex>
                            </Button>
                            
                            <Button
                              bg="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                              color="white"
                              h="56px"
                              width="100%"
                              borderRadius="xl"
                              fontSize="md"
                              fontWeight="700"
                              onClick={() => handleDownloadPolicy(successInfo.transactionId, successInfo.emissions![1].id)}
                              disabled={downloadingEmissionId === successInfo.emissions![1].id}
                              _hover={{ 
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 30px rgba(139, 92, 246, 0.35)"
                              }}
                              _active={{
                                transform: "translateY(0px)"
                              }}
                              boxShadow="0 6px 20px rgba(139, 92, 246, 0.25)"
                              transition="all 0.2s ease"
                            >
                              <Flex alignItems="center" gap={2}>
                                {downloadingEmissionId === successInfo.emissions![1].id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                                <Text>{downloadingEmissionId === successInfo.emissions![1].id ? "Baixando..." : `Baixar Apólice Upgrade (${successInfo.emissions![1].policyNo})`}</Text>
                              </Flex>
                            </Button>
                          </VStack>
                        </>
                      ) : (
                        <>
                          {/* Não há upgrade - mostrar apenas 1 botão */}
                          <Button
                            bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                            color="white"
                            h="56px"
                            borderRadius="xl"
                            fontSize="md"
                            fontWeight="700"
                            onClick={() => handleDownloadPolicy(successInfo.transactionId)}
                            disabled={isLoading}
                            _hover={{ 
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 30px rgba(16, 185, 129, 0.35)"
                            }}
                            _active={{
                              transform: "translateY(0px)"
                            }}
                            boxShadow="0 6px 20px rgba(16, 185, 129, 0.25)"
                            transition="all 0.2s ease"
                          >
                            <Flex alignItems="center" gap={2}>
                              {isLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              )}
                              <Text>{isLoading ? "Baixando..." : "Baixar Apólice"}</Text>
                            </Flex>
                          </Button>
                        </>
                      )}

                      {/* Botão para Nova Busca */}
                      <Button
                        bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                        color="white"
                        h="56px"
                        borderRadius="xl"
                        fontSize="md"
                        fontWeight="700"
                        onClick={handleNewSearch}
                        _hover={{ 
                          transform: "translateY(-2px)",
                          boxShadow: "0 12px 30px rgba(59, 130, 246, 0.35)"
                        }}
                        _active={{
                          transform: "translateY(0px)"
                        }}
                        boxShadow="0 6px 20px rgba(59, 130, 246, 0.25)"
                        transition="all 0.2s ease"
                      >
                        Nova Busca
                      </Button>
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            )}

          </VStack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
